import { useEffect, useState } from "react";
import axios from "axios";
import { Auth } from "../component/Auth";
import { Link } from "react-router-dom";
import {
  Box,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Select,
  HStack,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  Text,
  Flex,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { MdOutlineComment } from "react-icons/md";
import Timeline from "../component/Timeline";

export default function Inventory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inventories, setInventories] = useState([]);
  const [filters, setFilters] = useState({
    orderName: searchParams.get("orderName") || "",
    inventoryName: searchParams.get("inventoryName") || "",
    productName: searchParams.get("productName") || "",
    customerName: searchParams.get("customerName") || "",
    status: searchParams.get("status") || "",
    isExtended: searchParams.get("isExtended") === "true" || false
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [newRlNumber, setNewRlNumber] = useState("");
  const toast = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const statusOptions = [
    "ORDER_CREATED",
    "ORDER_PUBLISHED",
    "ORDER_DRAFT",
    "ORDER_VERIFIED",
    "ORDER_DELIVERED",
    "ORDER_CANCELED",
    "ORDER_OUT_FOR_DELIVERY",
    "ORDER_TRANSIT",
    "ORDER_RETURNED",
    "ORDER_PENDING_FOR_FULFILLMENT",
    "ORDER_READY_FOR_FULFILLMENT",
    "ORDER_PICKED",
    "TERMINATED",
    "AWAITING_VERIFICATION",
    "COMPLETED",
    "ACTIVE",
    "PAYMENT_PENDING",
    "ENDING_TOMORROW",
    "ENDED",
    "EARLY_CLOSURE"
  ];

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentPage(1); // Reset to first page
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const clearFilters = () => {
    setCurrentPage(1); // Reset to first page
    setFilters({
      orderName: "",
      inventoryName: "",
      productName: "",
      customerName: "",
      status: "",
      isExtended: false
    });
    setSearchParams({});
  };

  useEffect(() => {
    fetchInventories();
  }, [currentPage, filters]);

  const fetchInventories = async () => {
    // Construct query string from filters
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value);
      }
    });
    
    // Add pagination parameters
    queryParams.append('page', currentPage);
    queryParams.append('limit', pageSize);

    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_RALCO_API_URL}/api/inventory?${queryParams.toString()}`,
      headers: {
        "Content-Type": "application/json",
        Authorization: Auth(),
      },
    };

    const response = await axios.request(config);
    setInventories(response.data.data); // Update to access data array
    setTotalCount(response.data.pagination.totalCount);
  };

  useEffect(() => {
    fetchInventories();
  }, []); // Initial fetch

  // Add status string formatter function
  const convertStatus = (str) => {
    // Split the string by underscores
    var words = str.split("_");

    // Capitalize the first letter of each word
    for (var i = 0; i < words.length; i++) {
      words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1).toLowerCase();
    }

    // Join the words with a space
    return words.join(" ");
  };

  const handleEditClick = (inventory) => {
    setSelectedInventory(inventory);
    setNewRlNumber(inventory.inventoryName);
    setIsEditModalOpen(true);
  };

  const updateRlNumber = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_RALCO_API_URL}/api/inventory/${selectedInventory.id}`,
        { inventoryName: newRlNumber },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: Auth(),
          },
        }
      );

      // Update local state
      setInventories(inventories.map(inv => 
        inv.id === selectedInventory.id ? { ...inv, inventoryName: newRlNumber } : inv
      ));

      toast({
        title: "Success",
        description: "Inventory name updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setIsEditModalOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update inventory name",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Add pagination handler
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Add this function to handle comments count
  const commentsCount = (inventory) => {
    
    const count = inventory.OrderHeader.OrderStatusHistory?.filter(
      (x) => x.orderStatusToId === "ORDER_COMMENTED"
    ).length || 0;
    
    return (
      <Flex
        style={{ visibility: count > 0 ? "visible" : "hidden" }}
        onClick={(e) => {
          e.preventDefault();
          setSelectedOrder(inventory.OrderHeader);
          setIsTimelineOpen(true);
        }}
        cursor="pointer"
      >
        <MdOutlineComment color={"#3182ce"} fontSize={"22px"} />
        <Text ml={2}>{count}</Text>
      </Flex>
    );
  };

  // Add isExtended helper function
  const isExtended = (inventory) => {
    console.log(inventory.OrderHeader.OrderItem?.[0]?.TenureItem?.[0]?.tenure?.isExtended);
    return inventory.OrderHeader.OrderItem?.[0]?.TenureItem?.[0]?.tenure?.isExtended;
  };

  return (
    <Box p={5} flex="1" w={"100%"}>
      {/* Add Drawer component */}
      <Drawer
        isOpen={isTimelineOpen}
        placement="right"
        onClose={() => {
          setIsTimelineOpen(false);
          fetchInventories(); // Refresh data after closing
        }}
        size="md"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Comments & Timeline</DrawerHeader>
          <DrawerBody>
            {selectedOrder && (
              <Timeline
                orderData={selectedOrder}
                orderName={selectedOrder.OrderName}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Filters Section */}
      <Box mb={4} p={4} bg="white" borderRadius="md" shadow="sm">
        <HStack spacing={4} wrap="wrap">
          <FormControl maxW="200px">
            <FormLabel fontSize="sm">Order Name</FormLabel>
            <Input
              size="sm"
              name="orderName"
              value={filters.orderName}
              onChange={handleFilterChange}
              placeholder="Search order name"
            />
          </FormControl>

          <FormControl maxW="200px">
            <FormLabel fontSize="sm">Inventory Name (RL no)</FormLabel>
            <Input
              size="sm"
              name="inventoryName"
              value={filters.inventoryName}
              onChange={handleFilterChange}
              placeholder="Search inventory"
            />
          </FormControl>

          <FormControl maxW="200px">
            <FormLabel fontSize="sm">Product Name</FormLabel>
            <Input
              size="sm"
              name="productName"
              value={filters.productName}
              onChange={handleFilterChange}
              placeholder="Search product"
            />
          </FormControl>

          <FormControl maxW="200px">
            <FormLabel fontSize="sm">Customer Name</FormLabel>
            <Input
              size="sm"
              name="customerName"
              value={filters.customerName}
              onChange={handleFilterChange}
              placeholder="Search customer"
            />
          </FormControl>

          <FormControl maxW="200px">
            <FormLabel fontSize="sm">Status</FormLabel>
            <Select
              size="sm"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              placeholder="Select status"
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {convertStatus(status)}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl maxW="200px">
            <FormLabel fontSize="sm">&nbsp;</FormLabel>
            <HStack>
              <input
                type="checkbox"
                id="isExtended"
                name="isExtended"
                checked={filters.isExtended}
                onChange={handleFilterChange}
              />
              <FormLabel htmlFor="isExtended" fontSize="sm" mb={0}>
                Is Extended
              </FormLabel>
            </HStack>
          </FormControl>

          <FormControl maxW="200px">
            <FormLabel fontSize="sm">&nbsp;</FormLabel>
            <HStack>
              <Button size="sm" variant="outline" onClick={clearFilters}>
                Clear
              </Button>
            </HStack>
          </FormControl>
        </HStack>
      </Box>

      {/* Record count and pagination display */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Text fontSize="sm" color="gray.600" fontWeight="medium">
          {totalCount > 0 
            ? `Showing ${((currentPage - 1) * pageSize) + 1} - ${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} records`
            : 'No records found'
          }
        </Text>
        {totalCount > 0 && (
          <HStack spacing={2}>
            <Button
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 1}
              variant="outline"
              colorScheme="gray"
              leftIcon={<ChevronLeftIcon />}
            >
              Previous
            </Button>
            
            <Text fontSize="sm" color="gray.600">
              Page <strong>{currentPage}</strong> of <strong>{Math.ceil(totalCount / pageSize)}</strong>
            </Text>
            
            <Button
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={currentPage >= Math.ceil(totalCount / pageSize)}
              variant="outline"
              colorScheme="gray"
              rightIcon={<ChevronRightIcon />}
            >
              Next
            </Button>
          </HStack>
        )}
      </Box>

      <TableContainer height={`${window.innerHeight - 244}px`} overflowY={"scroll"}>
        <Table variant={"simple"} size={"xs"}>
          <Thead bgColor={"gray.100"} position={"sticky"} top={"0px"} zIndex={"99"}>
            <Tr bgColor={"#E2DFDF"}>
              <Th
                width="120px"
                fontSize={{
                  base: "xs",
                  md: "xs",
                  lg: "xs",
                  xl: "xs",
                  "2xl": "sm",
                }}
                py={3}
                color={"#25282B"}
              >
                Order #
              </Th>
              <Th
                width="180px"
                fontSize={{
                  base: "xs",
                  md: "xs",
                  lg: "xs",
                  xl: "xs",
                  "2xl": "sm",
                }}
                py={3}
                color={"#25282B"}
              >
                Created At
              </Th>
              <Th
                width="150px"
                fontSize={{
                  base: "xs",
                  md: "xs",
                  lg: "xs",
                  xl: "xs",
                  "2xl": "sm",
                }}
                py={3}
                color={"#25282B"}
              >
                Customer Name
              </Th>
              <Th
                width="200px"
                fontSize={{
                  base: "xs",
                  md: "xs",
                  lg: "xs",
                  xl: "xs",
                  "2xl": "sm",
                }}
                py={3}
                _first={{ paddingLeft: "20px" }}
                color={"#25282B"}
              >
                Inventory Name (RL no)
              </Th>
              <Th
                width="200px"
                fontSize={{
                  base: "xs",
                  md: "xs",
                  lg: "xs",
                  xl: "xs",
                  "2xl": "sm",
                }}
                py={3}
                color={"#25282B"}
              >
                Product Name
              </Th>
              <Th
                width="120px"
                fontSize={{
                  base: "xs",
                  md: "xs",
                  lg: "xs",
                  xl: "xs",
                  "2xl": "sm",
                }}
                py={3}
                color={"#25282B"}
              >
                Order Status
              </Th>
              <Th
                width="120px"
                fontSize={{
                  base: "xs",
                  md: "xs",
                  lg: "xs",
                  xl: "xs",
                  "2xl": "sm",
                }}
                py={3}
                color={"#25282B"}
              >
                Tenure End Date
              </Th>
              <Th
                width="80px"
                fontSize={{
                  base: "xs",
                  md: "xs",
                  lg: "xs",
                  xl: "xs",
                  "2xl": "sm",
                }}
                py={3}
                color={"#25282B"}
              >
                Comments
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {inventories.length > 0 ? (
              inventories.map((inventory) => (
                <Tr key={inventory.id} bgColor={"white"}>
                  <Td
                    width="120px"
                    fontSize={{
                      base: "xs",
                      md: "sm",
                      lg: "sm",
                      xl: "sm",
                      "2xl": "sm",
                    }}
                    py={3}
                    whiteSpace="normal"
                  >
                    <Link to={`/orders/order/${inventory.OrderHeader.id}`} style={{ textDecoration: "underline", color: "#3182ce" }}>{inventory.OrderHeader.OrderName}</Link>
                  </Td>
                  <Td
                    width="180px"
                    fontSize={{
                      base: "xs",
                      md: "sm",
                      lg: "sm",
                      xl: "sm",
                      "2xl": "sm",
                    }}
                    py={3}
                    whiteSpace="normal"
                  >
                    {new Intl.DateTimeFormat("en-US", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                      hour12: true,
                      timeZone: "ASIA/KOLKATA",
                    }).format(new Date(inventory.OrderHeader.placedDate))}
                  </Td>
                  <Td
                    width="150px"
                    fontSize={{
                      base: "xs",
                      md: "sm",
                      lg: "sm",
                      xl: "sm",
                      "2xl": "sm",
                    }}
                    py={3}
                    whiteSpace="normal"
                  >
                    {`${inventory.Party.firstName} ${inventory.Party.lastName}`}
                  </Td>
                  <Td
                    width="200px"
                    fontSize={{
                      base: "xs",
                      md: "sm",
                      lg: "sm",
                      xl: "sm",
                      "2xl": "sm",
                    }}
                    py={3}
                    pl={5}
                    cursor="pointer"
                    _hover={{ textDecoration: "underline" }}
                    onClick={() => handleEditClick(inventory)}
                    whiteSpace="normal"
                  >
                    {inventory.inventoryName || "N/A"}
                  </Td>
                  <Td
                    width="200px"
                    fontSize={{
                      base: "xs",
                      md: "sm",
                      lg: "sm",
                      xl: "sm",
                      "2xl": "sm",
                    }}
                    py={3}
                    whiteSpace="normal"
                  >
                    {inventory.productName}
                  </Td>
                  <Td
                    width="120px"
                    fontSize={{
                      base: "xs",
                      md: "sm",
                      lg: "sm",
                      xl: "sm",
                      "2xl": "sm",
                    }}
                    py={3}
                  >
                    <Button
                      size={"xs"}
                      cursor={"default"}
                      colorScheme={
                        (inventory.OrderHeader.orderStatusId === "ORDER_CREATED" && "green") ||
                        (inventory.OrderHeader.orderStatusId === "ORDER_PUBLISHED" && "facebook") ||
                        (inventory.OrderHeader.orderStatusId === "ORDER_DRAFT" && "purple") ||
                        (inventory.OrderHeader.orderStatusId === "ORDER_VERIFIED" && "whatsapp") ||
                        (inventory.OrderHeader.orderStatusId === "ORDER_DELIVERED" && "yellow") ||
                        (inventory.OrderHeader.orderStatusId === "ORDER_CANCELED" && "red") ||
                        (inventory.OrderHeader.orderStatusId === "ORDER_OUT_FOR_DELIVERY" && "orange") ||
                        (inventory.OrderHeader.orderStatusId === "ORDER_TRANSIT" && "messenger") ||
                        (inventory.OrderHeader.orderStatusId === "ORDER_RETURNED" && "telegram") ||
                        (inventory.OrderHeader.orderStatusId === "ORDER_PENDING_FOR_FULFILLMENT" && "teal") ||
                        (inventory.OrderHeader.orderStatusId === "ORDER_READY_FOR_FULFILLMENT" && "linkedin") ||
                        (inventory.OrderHeader.orderStatusId === "ORDER_PICKED" && "pink") ||
                        (inventory.OrderHeader.orderStatusId === "TERMINATED" && "blackAlpha") ||
                        (inventory.OrderHeader.orderStatusId === "AWAITING_VERIFICATION" && "black") ||
                        (inventory.OrderHeader.orderStatusId === "COMPLETED" && "blackAlpha") ||
                        (inventory.OrderHeader.orderStatusId === "ACTIVE" && "whatsapp") ||
                        (inventory.OrderHeader.orderStatusId === "PAYMENT_PENDING" && "red") ||
                        (inventory.OrderHeader.orderStatusId === "ENDING_TOMORROW" && "yellow") ||
                        (inventory.OrderHeader.orderStatusId === "ENDED" && "blackAlpha") ||
                        (inventory.OrderHeader.orderStatusId === "EARLY_CLOSURE" && "blackAlpha")
                      }
                    >
                      {`${convertStatus(inventory.OrderHeader.orderStatusId)
                        .split(" ")
                        .filter((d) => d !== "Order")
                        .join(" ")}${isExtended(inventory) ? "-Extended" : ""}`}
                    </Button>
                  </Td>
                  <Td
                    width="120px"
                    fontSize={{
                      base: "xs",
                      md: "sm",
                      lg: "sm",
                      xl: "sm",
                      "2xl": "sm",
                    }}
                    py={3}
                    whiteSpace="normal"
                  >
                    {inventory.OrderHeader.OrderItem?.[0]?.TenureItem?.[0]?.tenure?.endDate
                      ? new Intl.DateTimeFormat("en-US", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          timeZone: "ASIA/KOLKATA",
                        }).format(new Date(inventory.OrderHeader.OrderItem[0].TenureItem[0].tenure.endDate))
                      : "N/A"}
                  </Td>
                  <Td
                    width="80px"
                    fontSize={{
                      base: "xs",
                      md: "sm",
                      lg: "sm",
                      xl: "sm",
                      "2xl": "sm",
                    }}
                    py={3}
                  >
                    {commentsCount(inventory)}
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={8} textAlign="center" py={8}>
                  <Text fontSize="md" color="gray.500">
                    No records found
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Bottom pagination */}
      <Box mt={4} display="flex" justifyContent="center" alignItems="center">
        <HStack spacing={4}>
          <Text fontSize="sm" color="gray.600" fontWeight="medium">
            {totalCount > 0 
              ? `Showing ${((currentPage - 1) * pageSize) + 1} - ${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} records`
              : 'No records found'
            }
          </Text>
          {totalCount > 0 && (
            <HStack spacing={2}>
              <Button
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
                variant="outline"
                colorScheme="gray"
                leftIcon={<ChevronLeftIcon />}
              >
                Previous
              </Button>
              
              <Text fontSize="sm" color="gray.600">
                Page <strong>{currentPage}</strong> of <strong>{Math.ceil(totalCount / pageSize)}</strong>
              </Text>
              
              <Button
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage >= Math.ceil(totalCount / pageSize)}
                variant="outline"
                colorScheme="gray"
                rightIcon={<ChevronRightIcon />}
              >
                Next
              </Button>
            </HStack>
          )}
        </HStack>
      </Box>

      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Inventory Name</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>New RL Number</FormLabel>
              <Input
                value={newRlNumber}
                onChange={(e) => setNewRlNumber(e.target.value)}
                placeholder="Enter new RL number"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={updateRlNumber}>
              Save
            </Button>
            <Button variant="ghost" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>    
  );
}
